import { cloneElement, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";

import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

interface Props {
    show: boolean;
    children: React.ReactNode;
    header: React.ReactNode;
    footer?: React.ReactNode;
    onClose: () => void;
    className?: string;
    contentContainerClassName?: string;
    closeButton?: React.ReactElement<{ className?: string; width?: number }>;
    boxRef?: React.RefObject<HTMLDivElement>;
    backdrop?: boolean;
}

export const FloatingBox = ({
    boxRef,
    show,
    children,
    header,
    onClose,
    className,
    contentContainerClassName,
    closeButton,
    footer,
    backdrop,
}: Props) => {
    const content = (
        <>
            <button className="absolute right-1 top-1" onClick={onClose}>
                {closeButton ? (
                    cloneElement(closeButton, {
                        width: 24,
                        className: "text-taupe-gray",
                    })
                ) : (
                    <XMarkIcon width={24} className="text-taupe-grey" />
                )}
            </button>
            <div className="py-3 pl-2 pr-4">{header}</div>
            <div
                className={cx(
                    "border-platinum max-h-96 min-h-[128px] overflow-auto border-t px-4",
                    contentContainerClassName,
                )}
            >
                {children}
            </div>
            {footer && (
                <div className="border-platinum flex flex-row items-center justify-between border-t p-4">
                    {footer}
                </div>
            )}
        </>
    );
    const loadingUser = useFCSubscriptionStore((state) => state.loadingUser);
    const boxClassName = cx(
        "fixed bottom-0 right-0 z-[55] flex w-full flex-col items-stretch rounded-t bg-white shadow-lg ring-1 ring-black/5 sm:bottom-4 sm:right-4 sm:max-w-[350px] sm:rounded",
        className,
    );
    if (backdrop) {
        return (
            <Transition show={show && !loadingUser} as={Fragment}>
                <Dialog onClose={onClose} className="relative z-[10000]">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className="fixed inset-0 bg-black/30"
                            aria-hidden="true"
                        />
                    </Transition.Child>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4"
                        enterTo="translate-y-0 opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-4"
                    >
                        <Dialog.Panel ref={boxRef} className={boxClassName}>
                            {content}
                        </Dialog.Panel>
                    </Transition.Child>
                </Dialog>
            </Transition>
        );
    }

    return (
        <Transition
            ref={boxRef}
            show={show && !loadingUser}
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="translate-y-0 opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
            className={boxClassName}
        >
            {content}
        </Transition>
    );
};
